import React, { useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import {
  Box, Button, Collapse, HStack, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiSliders, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import useCompanyRnwy from '../../hooks/useCompanyRnwy';
import { parseCfg, RnwyCfg } from './rnwyCfg';
import RnwyCfgEditor from '../RnwyCfgEditor';

// item 637 — per-quote pricing. Each proposal owns a CFG SNAPSHOT (seeded from the Settings
// default on first save); editing here adjusts THIS quote's rates only, never the global rate
// card, and freezes with the proposal on approval. Collapsed by default — the 95% case never
// opens it (the seeded snapshot just works); it's here for "charge this client more/less".
const RnwyProposalCfgPanel: React.FC = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { rnwy } = useCompanyRnwy();
  const [open, setOpen] = useState(false);
  const accent = useColorModeValue('brandGreen.500', 'brandGreen.300');
  const accentBg = useColorModeValue('brandGreen.50', 'whiteAlpha.100');
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const proposalCfgJson = (values as { rnwyCfgJson?: string | null }).rnwyCfgJson;
  // this quote's snapshot if it has one, else the live global default (a brand-new quote)
  const cfg = parseCfg(proposalCfgJson || rnwy?.cfgJson);
  if (!cfg) return null;
  const onChange = (next: RnwyCfg) => form.change('rnwyCfgJson', JSON.stringify(next));

  return (
    <Box mt={4}>
      {/* item 647 — make the per-quote pricing toggle read clearly as a full-width expander button */}
      <Button
        w="100%"
        h="auto"
        py={3}
        variant="outline"
        borderColor={accent}
        color={accent}
        borderWidth="1px"
        onClick={() => setOpen((o) => !o)}
        _hover={{ bg: accentBg }}
        aria-expanded={open}
      >
        <HStack w="100%" justify="space-between" align="center">
          <HStack spacing={2}>
            <Box as={FiSliders} fontSize="lg" />
            <Text fontWeight="bold" fontSize="md">
              {open ? 'Hide quote pricing rates' : 'Adjust pricing rates for this quote'}
            </Text>
          </HStack>
          <Box as={open ? FiChevronUp : FiChevronDown} fontSize="lg" />
        </HStack>
      </Button>
      <Text fontSize="xs" color={sub} mt={2}>
        Tweaks the rate card for THIS proposal only — a snapshot of your Settings default. Your
        global rate card and every other quote are unchanged.
      </Text>
      <Collapse in={open} animateOpacity>
        <Box mt={3}>
          <RnwyCfgEditor value={cfg} onChange={onChange} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default RnwyProposalCfgPanel;
